import { reaction } from "mobx";
import { HYDRATABLE_ROUTES } from "./OverlayRouteMap";
import { deserializeFromHash, serializeToHash } from "./OverlayURLSerializer";
/**
 * Connects OverlayStack to the browser History API.
 *
 * The stack is the source of truth; the URL hash is a projection:
 * - push()    → history.pushState()   with new hash
 * - replace() → history.replaceState() with updated hash
 * - pop()     → history.pushState()   with shorter hash
 * - clear()   → history.pushState()   with empty hash
 * - popstate  → deserialize hash, restore stack (no history mutation)
 */
export class OverlayHistorySync {
    constructor(stack) {
        this.disposer = null;
        this.isHandlingPopstate = false;
        this.stack = stack;
        this.boundHandlePopstate = this.handlePopstate.bind(this);
    }
    /**
     * Hydrate stack from current URL hash and start listening for history changes.
     */
    start() {
        this.hydrateFromHash();
        window.addEventListener("popstate", this.boundHandlePopstate);
        this.disposer = this.watchStackChanges();
    }
    /**
     * Restore stack from the current URL hash, filtering to HYDRATABLE_ROUTES only.
     * Cleans up any stale non-hydratable entries from the URL.
     */
    hydrateFromHash() {
        const allHashEntries = deserializeFromHash(window.location.hash);
        const entries = allHashEntries.filter((e) => HYDRATABLE_ROUTES.has(e.route));
        if (entries.length > 0) {
            this.stack.restore(entries);
        }
        // Clean non-hydratable hash entries so they don't linger after refresh.
        const hasStaleHashEntries = allHashEntries.some((e) => !HYDRATABLE_ROUTES.has(e.route));
        if (hasStaleHashEntries) {
            history.replaceState(null, "", this.buildUrl(entries));
        }
    }
    /**
     * Watch stack changes and reflect them in the browser URL via pushState/replaceState.
     */
    watchStackChanges() {
        return reaction(() => ({
            entries: this.stack.entries,
            lastAction: this.stack.lastAction,
        }), ({ entries, lastAction }) => {
            // Skip: triggered by a popstate event (we're already handling URL change)
            // Skip: triggered by restore (same as popstate)
            // Skip: initial null (no action yet)
            if (this.isHandlingPopstate ||
                lastAction === "restore" ||
                lastAction === null) {
                return;
            }
            const newUrl = this.buildUrl(entries);
            if (lastAction === "replace") {
                history.replaceState(null, "", newUrl);
            }
            else {
                // push, pop, clear — all produce a new history entry
                history.pushState(null, "", newUrl);
            }
        });
    }
    /**
     * Stop syncing and remove all listeners.
     */
    stop() {
        window.removeEventListener("popstate", this.boundHandlePopstate);
        if (this.disposer !== null) {
            this.disposer();
            this.disposer = null;
        }
    }
    handlePopstate() {
        this.isHandlingPopstate = true;
        try {
            const entries = deserializeFromHash(window.location.hash);
            this.stack.restore(entries);
        }
        finally {
            this.isHandlingPopstate = false;
        }
    }
    /**
     * Build a URL string that puts overlay state in the hash fragment,
     * preserving the existing query string unchanged.
     */
    buildUrl(entries) {
        const hash = serializeToHash(entries);
        return (window.location.pathname +
            window.location.search +
            (hash ? "#" + hash : ""));
    }
}
